import { REDIRECT_URL_KEY } from "../configs/AppConfig";

// The intended destination after signing in lives only as ?redirect= on the
// auth URL. Two things lose it:
//   1. an SSO hop, which leaves the site and comes back on
//      /auth/{provider}/callback, where the param is gone;
//   2. moving between auth pages (login -> register), where the link has to
//      carry it forward itself.
// The SSO value is kept in sessionStorage rather than the OAuth `state` param,
// so the URL is never handed to Google or Microsoft (same reasoning as
// client_register_url).

const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

/** Same-origin paths only — a stored value must never become an open redirect. */
export const sanitizeRedirect = (target) => {
  if (!target || typeof target !== "string") return null;
  if (!target.startsWith("/")) return null;
  if (target.startsWith("//")) return null; // protocol-relative -> another host
  return target;
};

/** The ?redirect= of the page currently being viewed, if it is safe to use. */
export const getRedirectParam = () => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return sanitizeRedirect(params.get(REDIRECT_URL_KEY));
};

/**
 * A link to another auth page that keeps the destination alive, so signing in
 * or registering from there still lands the user where they were headed.
 */
export const authPathWithRedirect = (path) => {
  const target = getRedirectParam();
  return target
    ? `${path}?${REDIRECT_URL_KEY}=${encodeURIComponent(target)}`
    : path;
};

/**
 * Snapshot ?redirect= before handing off to an SSO provider. Always writes or
 * clears, so an abandoned attempt can't send the next sign-in somewhere stale.
 */
export const rememberPostLoginRedirect = () => {
  try {
    const target = getRedirectParam();
    if (target) {
      window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, target);
    } else {
      window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    }
  } catch {
    // Private mode / storage disabled — falls back to the default entry.
  }
};

/** The remembered destination, consumed on read. */
export const takePostLoginRedirect = () => {
  try {
    const stored = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (!stored) return null;
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return sanitizeRedirect(stored);
  } catch {
    return null;
  }
};
